//@flow
import React, {useState, useEffect} from 'react';
import {useHistory, useParams} from 'react-router-dom';

import {
  makeStyles,
  Typography,
  Divider,
  IconButton,
  Grid,
} from '@material-ui/core';
import {colors} from '../../../../../assets/styles/colors';
import {ArrowBack} from '@material-ui/icons';
import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';
import TiendadaRadioButton from '../../../../../elements/tiendada-radio-button';
import {useInitializeCrmCustomerCategories} from '../../../../crm/utils';
import SegmentFilters from './filters';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';

import type {CrmSegmentFilter} from '../../../../../entities/crm/segment';
import {useSegmentDetail, useCrmSegmentActions} from '../../../utils';
import InlineLoader from '../../../../../elements/LoadingSpinner/InlineLoader';

const useStyles = makeStyles(theme => ({
  container: {
    width: '100%',
    display: 'flex',
    padding: '1rem',
    flexDirection: 'column',
    gap: '1rem',
  },
  header: {
    maxWidth: 1200,
    background: '#f8f9fb',
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'black',
  },
  backButton: {
    background: 'white',
    padding: '4px',
    margin: '0 16px 0 0',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  sectionDescription: {
    marginTop: theme.spacing(2),
    color: colors.grey700,
    fontSize: '0.875rem',
  },
  root: {
    width: '100%',
    padding: '1rem',
    background: 'white',
    boxShadow: '0px 0px 1px rgba(40, 41, 61, 0.08)',
    borderRadius: '.5rem',
    minHeight: '500px',
    maxWidth: 1200,
  },
  sectionContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    gap: '1rem',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      alignItems: 'center',
    },
  },
  section: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'flex-start',
    gap: '.5rem',
    position: 'relative',
  },
  sectionInformation: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
  },
  subtitle: {
    fontSize: '.875rem',
    fontWeight: 600,
  },
  description: {
    fontSize: '.875rem',
    color: colors.grey700,
  },
  divider: {
    margin: '1rem 0',
  },
  uploadArea: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    aspectRatio: '3/2',
    background: colors.grey100,
    borderRadius: '.5rem',
    border: `1px solid ${colors.grey300}`,
    maxHeight: '200px',
    gap: '.5rem',
    flexDirection: 'column',
    color: colors.primary,
    cursor: 'pointer',
  },
  icon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  caption: {
    fontSize: '.75rem',
    color: colors.grey700,
    textAlign: 'center',
  },
  loader: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1,
    background: 'rgba(255, 255, 255, .8)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorMessage: {
    color: colors.error,
    fontSize: '.875rem',
    marginBlock: '1rem',
  },
}));

const MAIN_FILTER: CrmSegmentFilter = {
  filterType: 'ORDER_CREATED',
  conditions: {
    hasPurchased: true,
    days: 30,
  },
};

export default function CrmSegmentDynamicForm() {
  const classes = useStyles();

  const {id} = useParams();

  const history = useHistory();
  useInitializeCrmCustomerCategories();

  const isNew = id === 'new';
  const {segment, loading: isGettingSegments} = useSegmentDetail(id ?? 'new');

  const [segmentName, setSegmentName] = useState('');
  const [filter, setFilter] = useState(MAIN_FILTER);
  const [userType, setUserType] = useState('CLIENTS');
  const [errorMessage, setErrorMessage] = useState('');

  const {createDynamicSegment, loading, editSegment} = useCrmSegmentActions();

  useEffect(() => {
    if (!segment) return;
    setSegmentName(segment.name);
    if (!segment.filters) return;
    setFilter(segment.filters);
  }, [segment]);

  const handleSubmit = () => {
    if (!segmentName) {
      setErrorMessage('Por favor ingresa un nombre para tu segmento');
      return;
    }

    if (segment) {
      editSegment(segment.id, segmentName).then(result => {
        if (!result) return;
        history.push('/segments');
      });
      return;
    }

    createDynamicSegment(segmentName, filter).then(result => {
      if (!result) return;

      history.push(`/segments`);
    });
  };

  useEffect(() => {
    if (!errorMessage) return;
    const timeout = setTimeout(() => {
      setErrorMessage('');
    }, 5000);

    return () => {
      clearTimeout(timeout);
    };
  }, [errorMessage]);

  return (
    <div className={classes.container}>
      <header className={classes.header}>
        <Typography className={classes.title}>
          <IconButton
            className={classes.backButton}
            onClick={() => {
              history.push('/segments');
            }}
          >
            <ArrowBack />
          </IconButton>
          Creación de Segmento
        </Typography>
      </header>
      <aside className={classes.root}>
        {loading && (
          <div className={classes.loader}>
            <InlineLoader loading={loading} />
          </div>
        )}

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <section className={classes.sectionContainer}>
              <div className={classes.sectionInformation}>
                <Typography className={classes.subtitle}>1. Nombre</Typography>
              </div>
              <CustomTextField
                label="Nombre de segmento"
                value={segmentName}
                onChange={e => setSegmentName(e.target.value)}
              />
            </section>
          </Grid>
          <Grid item xs={6}>
            <section className={classes.sectionContainer}>
              <div className={classes.sectionInformation}>
                <Typography className={classes.subtitle}>
                  2. Definir tipo de usuario{' '}
                </Typography>
              </div>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TiendadaRadioButton
                    name="user-type"
                    checked={userType === 'CLIENTS'}
                    onChange={() => setUserType('CLIENTS')}
                    label="Clientes"
                  />
                </Grid>
                {/*   <Grid item xs={6}>
                    <TiendadaRadioButton
                      name="user-type"
                      checked={userType === 'LEADS'}
                      onChange={() => setUserType('LEADS')}
                      label="Leads"
                    />
                  </Grid> */}
              </Grid>
            </section>
          </Grid>

          <Grid item xs={12}>
            <Divider className={classes.divider} />
          </Grid>
          <Grid item xs={12}>
            <section className={classes.sectionContainer}>
              <div className={classes.sectionInformation}>
                <Typography className={classes.subtitle}>
                  3. Definir caso
                </Typography>
              </div>
            </section>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {!isGettingSegments && (
                <SegmentFilters
                  filter={filter}
                  onChange={setFilter}
                  disabled={!isNew || loading}
                />
              )}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Divider className={classes.divider} />
          </Grid>
          <Grid item xs={12}>
            {errorMessage && (
              <Typography className={classes.errorMessage}>
                {errorMessage}
              </Typography>
            )}
          </Grid>
          <Grid item xs={6}>
            <CustomButton variant="contained" onClick={handleSubmit}>
              Guardar segmento
            </CustomButton>
          </Grid>
        </Grid>
      </aside>
    </div>
  );
}
